/* eslint-disable react/no-unstable-nested-components */
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import getYear from "date-fns/getYear";
import numeral from "numeral";
import { useMemo } from "react";
import ClipLoader from "react-spinners/ClipLoader";
import { FormattedMessage, useIntl } from "react-intl";

import classNames from "components/ui/classNames";
import { ScrollArea } from "components/ui/ScrollArea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "components/ui/Select";

import { palette } from "lib/miolo";
import formatDateOrReturnHyphen from "lib/utils/formatDateOrReturnHyphen";
import notUndefinedOrNull from "lib/utils/notUndefinedOrNull";
import parseDateYmd from "lib/utils/parseDateYmd";
import type { IStockDividendsCorporateAction } from "types/graphqlTypes";

interface IBrazilianStockDividendsPaidTabContetProps {
  data?: IStockDividendsCorporateAction[];
  loading: boolean;
  filterStockDividendsByYear?: number | null;
  setFilterStockDividendsByYear: (value: number | null) => void;
}

export default function BrazilianStockDividendsPaidTabContet({
  data,
  loading,
  filterStockDividendsByYear,
  setFilterStockDividendsByYear,
}: IBrazilianStockDividendsPaidTabContetProps) {
  const intl = useIntl();
  const columnHelper = createColumnHelper<IStockDividendsCorporateAction>();

  const availableYears = useMemo(
    () => [
      ...new Set(
        data
          ?.map((dividend) => {
            if (dividend.excludingDate) {
              return getYear(parseDateYmd(dividend.excludingDate));
            }

            return undefined;
          })
          .filter(notUndefinedOrNull),
      ),
    ],
    [data],
  );

  const arrayOfDataFilteredByYear = useMemo(
    () =>
      data?.filter((node) => {
        if (!filterStockDividendsByYear) {
          return true;
        }

        return getYear(parseDateYmd(node.excludingDate)) === filterStockDividendsByYear;
      }),
    [data, filterStockDividendsByYear],
  );

  const columns = [
    columnHelper.accessor("tickerSymbol", {
      header: () => (
        <div
          className={classNames(
            "w-24 bg-slate-50 px-2 py-1.5 text-sm font-semibold text-slate-700",
            "dark:bg-slate-800 dark:text-slate-300",
          )}
        >
          <FormattedMessage defaultMessage="Ação" id="5xkzgU" />
        </div>
      ),
      cell: (info) => (
        <div
          className={classNames(
            "w-24 select-none",
            "whitespace-nowrap",
            "bg-slate-50 text-slate-600",
            "px-2 py-1.5 text-sm font-semibold text-slate-600",
            "group-hover:bg-violet-50 group-hover:text-violet-700",
          )}
        >
          <span>{info.getValue()}</span>
        </div>
      ),
      meta: {
        header: {
          className: classNames("sticky left-0 w-24"),
        },
      },
    }),
    columnHelper.accessor("excludingDate", {
      header: () => (
        <div className="overflow-hidden text-ellipsis px-4 py-1.5 text-sm font-semibold text-slate-600">
          <FormattedMessage defaultMessage="Data de Conversão" id="O5KXIR" />
        </div>
      ),
      cell: (info) => (
        <div
          className={classNames(
            "whitespace-nowrap",
            "px-4 py-1.5 text-sm font-normal",
            "text-slate-600 dark:text-slate-400",
            "hover:bg-teal-300/25 dark:hover:bg-slate-500/40",
          )}
        >
          <span>{formatDateOrReturnHyphen(info.getValue())}</span>
        </div>
      ),
    }),
    columnHelper.accessor("sharePaid", {
      header: () => (
        <div className="overflow-hidden text-ellipsis px-4 py-1.5 text-sm font-semibold text-slate-600">
          <FormattedMessage defaultMessage="Bonificação" id="UjC0PD" />
        </div>
      ),
      cell: (info) => {
        const value = info.getValue();

        return (
          <div
            className={classNames(
              "whitespace-nowrap",
              "px-4 py-1.5 text-sm font-normal",
              "text-slate-600 dark:text-slate-400",
              "hover:bg-teal-300/25 dark:hover:bg-slate-500/40",
            )}
          >
            <span>{value ? `${formatValue(value * 100)}%` : "-"}</span>
          </div>
        );
      },
    }),
  ];

  const table = useReactTable({
    data: arrayOfDataFilteredByYear || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (!data && !filterStockDividendsByYear) {
    return null;
  }

  if (loading) {
    return <ClipLoader size={28} color={palette.royalBlue} loading={true} />;
  }

  return (
    <div className="flex flex-col items-end space-y-2">
      <div className="w-full md:w-52">
        <Select
          value={filterStockDividendsByYear ? String(filterStockDividendsByYear) : ""}
          onValueChange={(value) => {
            if (!value) {
              return setFilterStockDividendsByYear(null);
            }

            return setFilterStockDividendsByYear(parseInt(value, 10));
          }}
        >
          <SelectTrigger
            className={classNames(
              "font-aeonik",
              "w-full rounded-lg",
              "border border-slate-200 bg-white",
              "text-sm font-medium tracking-wide text-slate-600",
              "disabled:select-none",
              "hover:border-transparent hover:ring-4 hover:ring-slate-100 hover:ring-offset-1 hover:ring-offset-slate-300",
              "focus-visible:border-transparent focus-visible:ring-4 focus-visible:ring-indigo-100 focus-visible:ring-offset-1 focus-visible:ring-offset-indigo-300",
              "truncate",
            )}
          >
            <SelectValue
              placeholder={intl.formatMessage({
                defaultMessage: "Filtre o ano",
                id: "0HA0Sr",
              })}
            />
          </SelectTrigger>
          <SelectContent className="rounded-lg border border-slate-200 text-sm" side="top">
            <SelectItem value="">
              {filterStockDividendsByYear === null
                ? intl.formatMessage({
                    defaultMessage: "Filtre o ano",
                    id: "0HA0Sr",
                  })
                : intl.formatMessage({
                    defaultMessage: "Remover filtro",
                    id: "J5Id85",
                  })}
            </SelectItem>

            {availableYears.map((year) => (
              <SelectItem key={`selectBoxYear/${year}`} value={String(year)} className="w-full">
                {year}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <ScrollArea type="always" orientation="horizontal" className="relative w-full">
        <div className="pb-4 lg:w-full">
          <div
            className={classNames("relative overflow-auto rounded-xl border border-slate-200", "dark:border-slate-950")}
          >
            <table className="w-full divide-y divide-slate-200 dark:divide-slate-700">
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr
                    key={headerGroup.id}
                    className="select-none divide-x divide-slate-200 dark:divide-slate-700 [&>th]:whitespace-nowrap"
                  >
                    {headerGroup.headers
                      .filter((header) => !header.column.columnDef.meta?.isHidden)
                      .map((header) => {
                        return (
                          <th
                            key={header.id}
                            className={classNames(
                              "m-0 p-0 text-center",
                              header.column.columnDef.meta?.header?.className,
                            )}
                          >
                            {header.isPlaceholder
                              ? null
                              : flexRender(header.column.columnDef.header, header.getContext())}
                          </th>
                        );
                      })}
                  </tr>
                ))}
              </thead>
              <tbody className="divide-y divide-slate-200 font-mono dark:divide-slate-700">
                {table.getRowModel().rows.map((row) => {
                  return (
                    <tr key={row.id} className="group divide-x divide-gray-200 dark:divide-slate-700">
                      {row
                        .getVisibleCells()
                        .filter((cell) => !cell.column.columnDef.meta?.isHidden)
                        .map((cell) => {
                          return (
                            <td
                              key={cell.id}
                              className={classNames("m-0 p-0 text-center", cell.column.columnDef.meta?.cell?.className)}
                            >
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </td>
                          );
                        })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </ScrollArea>
    </div>
  );
}

function formatValue(value?: number | null) {
  if (!value) {
    return "-";
  }

  return numeral(value).format("0.00").replace(".00", "").replace(".", ",");
}
